import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { OtpOutcome } from "./types";
import { LanguageToggle } from "./LanguageToggle";

export interface PhoneEntryStepProps {
  onSubmit: (
    phoneNumber: string,
    marketingConsent: boolean,
  ) => Promise<OtpOutcome>;
  isSubmitting: boolean;
}

const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

export const isValidPhoneNumber = (raw: string): boolean =>
  E164_PATTERN.test(raw.trim());

export const normalizePhoneNumber = (raw: string): string => {
  const withoutSeparators = raw.trim().replace(/[\s()-]/g, "");
  return withoutSeparators.startsWith("+")
    ? withoutSeparators
    : `+${withoutSeparators}`;
};

const RATE_LIMITED_CODE = "OT_005";
const RETRY_WAIT_NOT_REACHED_CODE = "OT_006";

export const PhoneEntryStep: React.FC<PhoneEntryStepProps> = ({
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation("sharedLogin");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showFormatError, setShowFormatError] = useState(false);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | undefined>(
    undefined,
  );
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const normalized = normalizePhoneNumber(phoneNumber);
    if (!isValidPhoneNumber(normalized)) {
      setShowFormatError(true);
      return;
    }
    setShowFormatError(false);
    setSubmitErrorCode(undefined);
    const result = await onSubmit(normalized, marketingOptIn);
    if (!result.success) {
      setSubmitErrorCode(result.errorCode ?? "UNKNOWN");
    }
  };

  return (
    <div className="flex flex-col gap-4 md:gap-6 rounded-2xl border-2 border-ink-12 bg-card p-5 md:p-8">
      <LanguageToggle />

      <h1 className="font-lexend text-[24px]/[28px] font-bold tracking-[-0.8px] text-ink-100">
        {t("phone.heading")}
      </h1>
      <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-ink-60">
        {t("phone.subtitle")}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label
            htmlFor="shared-login-phone"
            className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60"
          >
            {t("phone.phoneLabel")}
          </label>
          <input
            id="shared-login-phone"
            aria-label={t("phone.phoneLabel")}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder={t("phone.phonePlaceholder")}
            inputMode="tel"
            className="h-14 rounded-2xl border-2 border-ink-12 bg-app px-4 font-lexend text-[16px]/[20px] font-normal tracking-[-0.1px] text-ink-100 placeholder:text-ink-40"
          />
        </div>

        {showFormatError && (
          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
            {t("phone.errInvalidFormat")}
          </p>
        )}

        {submitErrorCode && (
          <p className="font-lexend text-[14px]/[18px] font-normal tracking-normal text-danger-ink">
            {submitErrorCode === RATE_LIMITED_CODE
              ? t("phone.errTooManySends")
              : submitErrorCode === RETRY_WAIT_NOT_REACHED_CODE
                ? t("phone.errRetryWaitNotReached")
                : t("phone.errSendFailed")}
          </p>
        )}

        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={marketingOptIn}
            onChange={(e) => setMarketingOptIn(e.target.checked)}
            aria-label={t("phone.marketingOptIn")}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-2 border-ink-12"
          />
          <span className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
            {t("phone.marketingOptIn")}
          </span>
        </label>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`flex h-14 w-full items-center justify-center rounded-2xl font-lexend text-[16px]/[20px] font-bold tracking-normal text-white ${
            isSubmitting ? "cursor-not-allowed bg-gray-300" : "bg-primary-100"
          }`}
        >
          {isSubmitting ? t("phone.submitting") : t("phone.submit")}
        </button>

        <p className="font-lexend text-[12px]/[16px] font-normal tracking-normal text-ink-60">
          {t("phone.disclosure")}
        </p>
      </form>
    </div>
  );
};
